import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  ButtonGroup,
  IconButton,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { RiDeleteBin6Line } from "@remixicon/react";
import { useRef } from "react";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { useMemberDeleteMutation } from "@/global-settings/data/useMemberDeleteMutation";
import { Member } from "@reflag/shared/organizationAPI";

type MemberActionsProps = {
  member: Member;
};

export function MemberActions({ member }: MemberActionsProps) {
  const { userId } = useEnvironment();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef(null);

  const isCurrentUser = member.id === userId;

  const { mutate: deleteMember, isPending: isDeleting } =
    useMemberDeleteMutation(member.id);

  const handleDelete = () => {
    deleteMember();
    onClose();
  };

  return (
    <>
      <IconButton
        aria-label="Remove member"
        colorScheme="red"
        icon={<RiDeleteBin6Line size={16} />}
        isDisabled={isCurrentUser}
        isLoading={isDeleting}
        size="sm"
        variant="ghost"
        onClick={onOpen}
      />

      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Remove member</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to remove{" "}
              <Text as="span" fontWeight="medium">
                {member.name}
              </Text>{" "}
              from the organization? This action cannot be undone.
            </AlertDialogBody>
            <AlertDialogFooter>
              <ButtonGroup>
                <Button
                  ref={cancelRef}
                  isDisabled={isDeleting}
                  variant="ghostDimmed"
                  onClick={onClose}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  isLoading={isDeleting}
                  onClick={handleDelete}
                >
                  Remove
                </Button>
              </ButtonGroup>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
